import React from 'react';
import {BoolSearchField} from '../fields/BoolField';
import {CheckboxField} from '../fields/CheckboxField';
import {ComboBoxField} from '../fields/ComboBoxField';
import {ItemField} from '../fields/ItemField';
import {MonthFilterField} from '../fields/MonthFilterField';
import {NumberField} from '../fields/NumberField';
import {RadioField} from '../fields/RadioField';
import {TextField} from '../fields/TextField';
import {Actions} from '../types/Action';
import {SelectWidget} from '../widgets/SelectWidget';
import {renderNull} from './CommonField';
import {DateFilterField} from './DateFilterField';
import {OnGetValue, OnSearch} from './FieldComponent';
import {FieldValueProps, FieldValues, OnChangeFieldValue} from './FieldValue';
import {TimeFilterField} from './TimeFilterField';
import {YearMonthField} from '../fields/YearMonthField';
import {USE_DEFAULT_LABEL} from '../widgets/ItemWidget';

export type FilterFieldValueProps = FieldValueProps & {
  onGetValue: OnGetValue;
  onSearch: OnSearch;
  actions: Actions;
};

function onChangeAndSearch(
  onChange: OnChangeFieldValue,
  onSearch: OnSearch,
): OnChangeFieldValue {
  return (vs: FieldValues, args?: any) => {
    onChange(vs, args);
    onSearch(vs);
  };
}

export function FilterFieldValue(props: FilterFieldValueProps) {
  const render =
    searchWidgetFunctions[props.field.searchWidget!] ||
    widgetFunctions[props.field.widget!] ||
    typeFunctions[props.field.type] ||
    renderTextField;

  return render(props);
}

const renderTextField = (props: FilterFieldValueProps) => {
  return (
    <TextField
      {...props}
      multiline={false}
      onKeyUp={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
          props.onSearch();
        }
      }}
    />
  );
};

const renderNumberField = (props: FilterFieldValueProps) => {
  return (
    <NumberField
      {...props}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
          props.onSearch();
        }
      }}
    />
  );
};

const renderRadioField = (props: FilterFieldValueProps) => {
  const fn = onChangeAndSearch(props.onChange, props.onSearch);
  return <RadioField {...props} vertical={true} onChange={fn} />;
};

const renderSelectField = (props: FilterFieldValueProps) => {
  const fn = onChangeAndSearch(props.onChange, props.onSearch);
  return <SelectWidget {...props} multiple={true} onChange={fn} />;
};

const renderSingleSelectField = (props: FilterFieldValueProps) => {
  const fn = onChangeAndSearch(props.onChange, props.onSearch);
  return <SelectWidget {...props} multiple={false} onChange={fn} />;
};

const renderCheckboxField = (props: FilterFieldValueProps) => {
  const fn = onChangeAndSearch(props.onChange, props.onSearch);
  return <CheckboxField {...props} onChange={fn} />;
};

const renderItemField = (props: FilterFieldValueProps) => {
  const fn = onChangeAndSearch(props.onChange, props.onSearch);

  // To avoid propagating unwanted props, {...props} are not used.
  // ItemField has a selection dialog and I want to prevent it from being propagated there.
  return (
    <ItemField
      value={props.value}
      onChange={fn}
      field={props.field}
      actions={props.actions}
      relatedResources={props.relatedResources}
      relatedSchemas={props.relatedSchemas}
      capacity={0}
      selectButtonLabel={USE_DEFAULT_LABEL}
      hideClearButton={false}
    />
  );
};

const renderDateField = (props: FilterFieldValueProps) => {
  const fn = onChangeAndSearch(props.onChange, props.onSearch);
  return <DateFilterField {...props} onChange={fn} />;
};

const renderTimeField = (props: FilterFieldValueProps) => {
  const fn = onChangeAndSearch(props.onChange, props.onSearch);
  return <TimeFilterField {...props} onChange={fn} />;
};

const renderMonthField = (props: FilterFieldValueProps) => {
  const fn = onChangeAndSearch(props.onChange, props.onSearch);
  return <MonthFilterField {...props} onChange={fn} />;
};

const renderBoolField = (props: FilterFieldValueProps) => {
  const fn = onChangeAndSearch(props.onChange, props.onSearch);
  return <BoolSearchField {...props} onChange={fn} />;
};

const renderComboBoxField = (props: FilterFieldValueProps) => {
  return <ComboBoxField {...props} />;
};

const renderYearMonthField = (props: FilterFieldValueProps) => {
  return (
    <YearMonthField
      {...props}
      onKeyUp={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
          props.onSearch();
        }
      }}
    />
  );
};

type RenderFilterFieldFn = (props: FilterFieldValueProps) => JSX.Element | null;

const searchWidgetFunctions: {[key: string]: RenderFilterFieldFn} = {
  string: renderTextField,
  text: renderTextField,
  date: renderDateField,
  checkbox: renderCheckboxField,
  select: renderSelectField,
  single_select: renderSingleSelectField,
  radio: renderRadioField,
  combobox: renderComboBoxField,
  scanner: renderNull,
  hidden: renderNull,
};

const widgetFunctions: {[key: string]: RenderFilterFieldFn} = {
  string: renderTextField,
  text: renderTextField,
  checkbox: renderCheckboxField,
  select: renderSelectField,
  single_select: renderSingleSelectField,
  radio: renderCheckboxField,
  month: renderMonthField,
  time: renderTimeField,
  combobox: renderComboBoxField,
  scanner: renderNull,
  hidden: renderNull,
};

const typeFunctions: {[key: string]: RenderFilterFieldFn} = {
  string: renderTextField,
  int: renderNumberField,
  float: renderNumberField,
  serial: renderTextField,
  date: renderDateField,
  date_list: renderDateField,
  year_month: renderYearMonthField,
  resid: renderItemField,
  resid_list: renderItemField,
  text: renderTextField,
  bool: renderBoolField,
};
